import { useMutation, useQuery } from '@apollo/client';
import {
  AlertService,
  Button,
  Checkbox,
  Modal,
  ModalContent,
  NoneSelectedIconLarge,
  Search,
  SpoilerAlertAppleIcon,
  Theme,
} from '@spoiler-alert/ui-library';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import appSettings from '../../../src/app-settings';
import { featureFlags } from '../../../src/enums';
import destinationType from '../../enums/staged-inventory-destination-type';
import { createStagedListingsFromInventory } from '../../graphql/mutations';
import { DiscountedSalesDistributionListQuery } from '../../graphql/queries';
import checkFeature from '../../helpers/check-feature-flag';
import { Store } from '../../store';
import HandlingMappingModalContent from './handling-mapping-modal-content';
import { User } from '../../graphql/query-result-types';

const styles = {
  modalContents: {
    height: '600px',
  },
  containerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    gap: 16,
    maxHeight: 585,
    maxWidth: 900,
    minHeight: 300,
  },
  searchInput: {
    margin: 0,
  },
  leftWrapper: {
    width: 570,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  listSelectionWrapper: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  listArea: {
    justifyContent: 'space-between',
    minHeight: 150,
    margin: 0,
    padding: [10, 0, 0],
    border: `solid 1px ${Theme.grey30}`,
    overflow: 'auto',
    borderTop: 'none',
    borderRadius: '0',
    flex: '1 1 245px',
  },
  mmListArea: {
    extend: 'listArea',
    borderTop: `solid 1px ${Theme.grey30}`,
    marginTop: 4,
    flex: '1 1 175px',
  },
  allListArea: {
    extend: 'distributionList',
    border: `solid 1px ${Theme.grey30}`,
    cursor: 'pointer',
    height: 44,
    '& input[type="checkbox"] + div': {
      marginLeft: 5,
    },
  },
  spoilerAlertBuyersArea: {
    justifyContent: 'space-between',
    flex: '1',
    padding: '16px',
    border: `solid 1px ${Theme.grey30}`,
    borderBottom: 'none',
    borderRadius: '0',
    margin: '12px 8px 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  spoilerAlertBuyersInfoArea: {
    justifyContent: 'space-between',
    flex: '1',
    padding: '16px',
    border: `solid 1px ${Theme.grey30}`,
    borderRadius: '0',
    margin: '12px 8px 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  spoilerAlertBuyersAreaHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 400,
    cursor: 'pointer',
    gap: 24,
  },
  spoilerAlertBuyersAreaInfoHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 16,
    fontWeight: 400,
    gap: 24,
  },
  spoilerAlertBuyersAreaCheckboxContainer: {
    display: 'flex',
  },
  spoilerAlertBuyersAreaContent: {
    marginLeft: 31,
    fontSize: 14,
    fontWeight: 400,
  },
  spoilerAlertBuyersAreaInfoContent: {
    fontSize: 16,
    fontWeight: 400,
    color: Theme.teal,
    cursor: 'pointer',
  },
  spoilerAlertBuyersAreaInfoContentClicked: {
    fontSize: 16,
    fontWeight: 400,
  },
  spoilerAlertBuyersAreaFooter: {
    backgroundColor: Theme.grey5,
    display: 'flex',
    margin: '0 8px 0 0',
    padding: '12px 12px 12px 47px',
    fontWeight: 500,
    fontSize: 14,
    alignItems: 'end',
    border: `solid 1px ${Theme.grey30}`,
    borderTop: 'none',
  },
  spoilerAlertApple: {
    minWidth: 22,
    minHeight: 24,
  },
  spoilerAlertAppleInline: {
    margin: '0px 4px',
    width: 14,
    height: 16,
  },
  distributionListText: {
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    textAlign: 'left',
  },
  distributionListTextAll: {
    extend: 'distributionListText',
  },
  buyerPreview: {
    padding: 0,
    borderRadius: 2,
    border: `1px solid ${Theme.grey30}`,
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 270px',
  },
  previewHeader: {
    flexGrow: 0,
    padding: [6, 10],
    margin: 0,
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.43em',
    letterSpacing: ' -0.08px',
    textAlign: 'left',
    color: Theme.text,
    borderBottom: `1px solid ${Theme.grey30}`,
  },
  distributionList: {
    display: 'flex',
    alignItems: 'center',
    padding: [6, 16],
    whiteSpace: 'nowrap',
    color: Theme.textColorPrimary,
    gap: 5,
    height: 36,
    '& input[type="checkbox"]:checked + div': {
      color: 'green',
    },
    width: '100%',
    cursor: 'pointer',
    '&:hover': {
      background: '#f8f9f9',
    },
    '&:focus': {
      border: 'solid 1px #4eaac1',
      background: '#f8f9f9',
    },
  },
  selectedDistributionList: {
    extend: 'distributionList',
    color: Theme.green,
  },
  noDistributionListsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: 200,
    flexGrow: 1,
  },
  noneSelectedIcon: {
    width: '165px',
    height: '79px',
    margin: 0,
    objectFit: 'contain',
  },
  noDistributionListsText: {
    height: 47,
    margin: [17, 20, 0, 11],
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.5em',
    letterSpacing: '-0.18px',
    textAlign: 'center',
  },
  noDistributionListSearch: {
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: '24px',
    letterSpacing: -0.18,
    padding: [0, 16],
  },
  noDistributionListSearchWrapper: {
    extend: 'listArea',
    borderTop: `solid 1px ${Theme.grey30}`,
  },
  buyerNames: {
    overflow: 'auto',
    flexGrow: 1,
    margin: 0,
    padding: [0, 12],
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.5em',
    letterSpacing: '-0.18px',
    textAlign: 'left',
    color: Theme.text,
    flex: '1 0 300px',
  },
  buyerGroups: {},
  buyerGroup: {
    padding: [10, 0],
    '& h4': {
      color: Theme.grey80,
      textTransform: 'uppercase',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '16px',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
    },
  },
  buyerName: {
    width: '100%',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '0 24px 24px 24px',
    alignItems: 'center',
  },
  checkbox: {
    height: 18,
    width: 24,
    textAlign: 'center',
    '&:checked': {
      color: 'green',
    },
  },
  allCheckbox: {
    extend: 'checkbox',
  },
  modalHeader: {
    width: '145px',
    height: '28px',
    flexGrow: 0,
    margin: '0 433px 24px 0',
    fontFamily: 'Roboto',
    fontSize: '24px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.17em',
    letterSpacing: 'normal',
    textAlign: 'left',
    color: '#000',
    alignSelf: 'flex-start',
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 500,
    margin: 0,
  },
  saBuyersCheckbox: {
    marginTop: 2,
    marginRight: 8,
  },
  mmFooterNotice: {
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
};

const useStyles = createUseStyles(styles, { name: 'ListInventoryModal' });

interface DistributionList {
  _id: string;
  name: string;
  destinationType: string;
  color?: string;
  isVendorOfRecord: boolean;
  buyers: {
    siteId: string;
    siteName: string;
  }[];
}

interface StagingItem {
  inventoryId?: string;
  distributionListId: string;
  destinationType: string;
}

interface InventoryFilter {
  [property: string]: unknown;
  sortBy: {
    asc: boolean;
    sortProperty: string;
  };
}

interface ListMutationReturnData {
  createStagedListingsFromInventory: {
    inventories: object[];
    stagedListings: object[];
    errors: {
      code: string;
      message: string;
    }[];
    progress: {
      _id: string;
      staged: boolean;
    };
  };
}

interface ListInventoryModalProps {
  onHide: (actionTaken: boolean, errorCount?: number, loggedCount?: number) => void;
  open: boolean;
  inventoryIds: string[];
  refetchQueries: string[];
  allSelected: boolean;
  filters: InventoryFilter;
  user: User;
  isDonation: boolean;
}

const ListInventoryModal: React.FC<ListInventoryModalProps> = ({
  onHide,
  open,
  inventoryIds,
  refetchQueries,
  allSelected,
  filters,
  user,
  isDonation,
}) => {
  const { loading, data } = useQuery<{
    currentUserQuery: {
      _id: string;
      site: {
        _id: string;
        distributionLists: DistributionList[];
      };
    };
  }>(DiscountedSalesDistributionListQuery, { fetchPolicy: 'no-cache' });
  const [selectedDistributionLists, setSelectedDistributionLists] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const spoilerAlertBuyersEnabled = checkFeature(featureFlags.certifiedBuyers);
  const classes = useStyles();

  const [CreateStagedListingsFromInventory, { loading: createStageListingLoading }] = useMutation<
    ListMutationReturnData,
    {
      inventoriesToStage: StagingItem[];
      allSelected: boolean;
      inventoryFilters: InventoryFilter;
    }
  >(createStagedListingsFromInventory);

  useEffect(() => {
    setSelectedDistributionLists(new Set());
  }, [open, loading]);

  const allLists = data?.currentUserQuery.site.distributionLists || [];

  useEffect(() => {
    if (open && inventoryIds.length > 0 && allLists.length === 1) {
      const selection = new Set([allLists[0]._id]);
      setSelectedDistributionLists(selection);
    }
  }, [allLists, open, inventoryIds]);

  const dlMap = useMemo(() => {
    return allLists.reduce((_map, dl) => {
      _map.set(dl._id, dl);
      return _map;
    }, new Map<string, DistributionList>());
  }, [allLists]);

  const distributionLists = useMemo(
    () =>
      isDonation
        ? allLists.filter((dl) => dl.destinationType.toLowerCase() === 'donation' && !dl.isVendorOfRecord) || []
        : allLists.filter((dl) => dl.destinationType.toLowerCase() !== 'donation' && !dl.isVendorOfRecord) || [],
    [isDonation, allLists],
  );

  const filteredDistributionLists = useMemo(
    () => distributionLists.filter((dl) => dl?.name?.toLowerCase().includes(searchTerm?.toLowerCase())),
    [distributionLists, searchTerm],
  );

  const mmDLs = useMemo(() => allLists?.filter((dl) => dl.isVendorOfRecord) || [], [allLists]);

  const selectAll = useMemo(() => {
    if (selectedDistributionLists.size === 0) return false;
    return distributionLists.every((dl) => selectedDistributionLists.has(dl._id));
  }, [selectedDistributionLists, distributionLists]);

  const toggleSelectAll = () => {
    const selectedMMLists = Array.from(selectedDistributionLists).filter((dlid) => {
      const dl = dlMap.get(dlid);
      if (dl?.isVendorOfRecord) return true;
      return false;
    });
    if (selectAll) {
      setSelectedDistributionLists(new Set(selectedMMLists));
    } else {
      const ids = new Set([...selectedMMLists, ...filteredDistributionLists.map((dl) => dl._id)]);
      setSelectedDistributionLists(ids);
    }
  };

  const toggleSelectDistributionList = (id: string) => {
    if (selectedDistributionLists.has(id)) {
      setSelectedDistributionLists((prev) => new Set(Array.from(prev).filter((dlId) => dlId !== id)));
    } else {
      setSelectedDistributionLists((prev) => new Set([...prev, id]));
    }
  };

  const cancel = () => {
    onHide(false);
  };

  const list = useCallback(
    async (listsToList: Set<string>) => {
      const inventoriesToStage: StagingItem[] = [];
      if (allSelected) {
        Array.from(listsToList).map((dl) =>
          inventoriesToStage.push({
            distributionListId: dl,
            destinationType: isDonation ? destinationType.donation : destinationType.discountedSales,
          }),
        );
      } else {
        inventoryIds.map((i) =>
          Array.from(listsToList).map((dl) =>
            inventoriesToStage.push({
              inventoryId: i,
              distributionListId: dl,
              destinationType: isDonation ? destinationType.donation : destinationType.discountedSales,
            }),
          ),
        );
      }
      const createPostVariables = {
        inventoriesToStage,
        allSelected,
        inventoryFilters: filters,
      };
      try {
        const response = await CreateStagedListingsFromInventory({
          variables: { ...createPostVariables },
          refetchQueries,
        });
        let actionTaken = false;
        if (response && response.data) {
          try {
            const loggedResults = response.data.createStagedListingsFromInventory;
            if (loggedResults.errors?.length > 0) {
              throw new Error(loggedResults.errors[0].message);
            }
            actionTaken = loggedResults.inventories?.length > 0;
            Store.refreshOfferComp(true);
            onHide(actionTaken, 0, loggedResults.inventories.length);
          } catch (error) {
            AlertService.alert({ type: 'warning', message: <span>{(error as Error).message}</span>, autoDismiss: false, dismissDelay: 1000 });
            onHide(false, 1, 0);
          }
        }
      } catch (error) {
        const message =
          'We were unable to create the listings due to an unknown error. Our team has been notified and are looking into the issue. Please contact customer support if the issue persists.';
        AlertService.alert({ type: 'warning', message: <span>{message}</span>, autoDismiss: false, dismissDelay: 1000 });
        // set logged count to 0 to avoid displaying a success message
        onHide(false, 1, 0);
      }
    },
    [CreateStagedListingsFromInventory, allSelected, filters, inventoryIds, isDonation, onHide, refetchQueries],
  );

  const searchingAndNoResults = () => {
    return filteredDistributionLists.length === 0 && searchTerm.length > 0;
  };

  const renderDLBuyers = (list: DistributionList) => {
    return (
      <div className={classes.buyerGroup} key={list._id}>
        <h4>
          {list.name} {list.isVendorOfRecord && <SpoilerAlertAppleIcon className={classes.spoilerAlertAppleInline} />}
        </h4>
        {list.buyers.sort().map((buyer) => (
          <div className={classes.buyerName} key={buyer.siteId}>
            {buyer.siteName}
          </div>
        ))}
      </div>
    );
  };

  const renderBuyersList = () => {
    return (
      <div>
        {Array.from(selectedDistributionLists).map((dlId) => {
          const list = dlMap.get(dlId);
          return list ? renderDLBuyers(list) : null;
        })}
      </div>
    );
  };

  const renderDLOptionRow = (dl: DistributionList) => {
    const selected = selectedDistributionLists.has(dl._id);
    return (
      <div
        onClick={() => toggleSelectDistributionList(dl._id)}
        className={selected ? classes.selectedDistributionList : classes.distributionList}
        key={dl._id}
        data-testid="checkbox-row"
      >
        <Checkbox className={classes.checkbox} onChecked={() => toggleSelectDistributionList(dl._id)} checked={selected} />
        <div className={classes.distributionListText}>
          {dl.name} ({dl.buyers.length})
        </div>
      </div>
    );
  };

  const renderSpoilerAlertBuyersArea = () => {
    return (
      <div className={classes.mmListArea} data-testid="mm-modal-list-area">
        {mmDLs.map((dl) => renderDLOptionRow(dl))}
      </div>
    );
  };

  return (
    <Modal className={classes.modalContents} onHide={onHide} open={open} closeOnEsc closeOnOutsideClick>
      {user.site.hasUnmappedHandlingValues ? (
        <HandlingMappingModalContent user={user} onHide={onHide} />
      ) : (
        <div>
          <ModalContent>
            <div className={classes.modalHeader}>Stage listings</div>
            <div className={classes.containerWrapper}>
              <div className={classes.leftWrapper}>
                {distributionLists.length > 0 && (
                  <>
                    {spoilerAlertBuyersEnabled && <h3 className={classes.sectionHeader}>My Customers</h3>}
                    <Search onChange={setSearchTerm} value={searchTerm} placeholder="Search" className={classes.searchInput} />
                    {!searchingAndNoResults() ? (
                      <div className={classes.listSelectionWrapper}>
                        <div data-testid="all-checkbox-row" data-element="select-all-row" onClick={toggleSelectAll} className={classes.allListArea}>
                          <Checkbox className={classes.allCheckbox} onChecked={toggleSelectAll} checked={selectAll} value="select-all" />
                          <div className={classes.distributionListTextAll}>All</div>
                        </div>
                        <div className={classes.listArea} data-testid="modal-list-area">
                          {filteredDistributionLists.map((dl) => renderDLOptionRow(dl))}
                        </div>
                      </div>
                    ) : (
                      <div className={classes.noDistributionListSearchWrapper}>
                        <div className={classes.noDistributionListSearch}> No distribution lists matched your search. </div>
                      </div>
                    )}
                  </>
                )}
                {spoilerAlertBuyersEnabled && (
                  <div className={classes.listSelectionWrapper}>
                    <h3 className={classes.sectionHeader}>Managed Marketplace</h3>
                    {renderSpoilerAlertBuyersArea()}
                  </div>
                )}
              </div>

              <div className={classes.buyerPreview}>
                <div className={classes.previewHeader}>Customer Preview</div>
                {(selectedDistributionLists.size === 0 && (
                  <div className={classes.noDistributionListsContainer}>
                    <NoneSelectedIconLarge className={classes.noneSelectedIcon} />
                    <p className={classes.noDistributionListsText}>Select a list (left) to see the associated customers.</p>
                  </div>
                )) || <div className={classes.buyerNames}>{renderBuyersList()}</div>}
              </div>
            </div>
          </ModalContent>
          <div className={classes.modalFooter}>
            {spoilerAlertBuyersEnabled && (
              <div className={classes.mmFooterNotice}>
                Managed Maretplace buyers will be marked with a <SpoilerAlertAppleIcon className={classes.spoilerAlertAppleInline} /> throughout the
                platform.
              </div>
            )}
            <Button type="button" onClick={cancel} secondary disabled={createStageListingLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => list(selectedDistributionLists)}
              disabled={selectedDistributionLists.size === 0}
              loading={createStageListingLoading}
              loadingText="Staging Listings"
              data-testid="stage-listings-button"
            >
              {pluralize('Stage Listing', inventoryIds.length)}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ListInventoryModal;
